

import React, { useState } from 'react';
import axios from 'axios';
import {
  TextField, Button, Typography, Snackbar,
  Box, IconButton, CircularProgress, Paper, Dialog,
  DialogActions, DialogContent,
  DialogTitle, Stack, styled, Tooltip, Container
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  AddCircleOutline as AddIcon,
  EmailOutlined as EmailIcon,
  ScheduleSend as ScheduleIcon,
  Send as SendIcon,
  Close as CloseIcon
} from '@mui/icons-material';

import PhotoRequestReview from './GuestPhotoUpload/PhotoRequestReview';

/* ─── Design Tokens ─── */
const T = {
  amber:    '#C2823C',
  amberLight:'#E8A85A',
  cream:    '#FAF8F4',
  ivory:    '#FFFEFB',
  ink:      '#1a1510',
  inkMid:   '#4a3f30',
  inkLight: '#8a7055',
  border:   'rgba(194,130,60,0.15)',
  shadow:   '0 12px 40px rgba(100,70,30,0.08)',
};

/* ─── Styled Components ─── */
const InvitePaper = styled(Paper)(({ theme }) => ({
  background: T.ivory,
  border: `1px solid ${T.border}`,
  borderRadius: '24px',
  padding: theme.spacing(4),
  boxShadow: T.shadow,
  maxWidth: '750px',
  margin: '20px auto',
}));

const GuestRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '16px',
  borderRadius: '16px',
  background: '#fff',
  border: `1px solid ${T.border}`,
  marginBottom: '12px',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(194,130,60,0.1)',
    transform: 'translateY(-2px)',
  },
}));

// FIX: use $primary (transient prop) to avoid passing unknown prop to DOM
const ActionButton = styled(Button)(({ $primary }) => ({
  borderRadius: '50px',
  padding: '10px 24px',
  textTransform: 'none',
  fontWeight: 600,
  fontFamily: "'DM Sans', sans-serif",
  background: $primary
    ? `linear-gradient(135deg, ${T.amber} 0%, ${T.amberLight} 100%)`
    : 'transparent',
  color: $primary ? '#fff' : T.inkMid,
  border: $primary ? 'none' : `1px solid ${T.border}`,
  '&:hover': {
    background: $primary ? T.amber : 'rgba(194,130,60,0.05)',
  },
}));

// FIX: added eventName to props
const AccessRequestForm = ({ eventId, userId, videoId, shareId, eventName, handleClose,onPhotosAdded }) => {
  const [contacts, setContacts] = useState([{ value: '', showSchedule: false, send_time: '' }]);
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  // FIX: tab state is now actually used to toggle between invite and photos views
  const [tab, setTab] = useState('invite');
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleInputChange = (index, value) => {
    const newContacts = [...contacts];
    if (!value.includes('@') && value.length === 10 && /^[0-9]+$/.test(value)) {
      newContacts[index].value = `+91${value}`;
    } else {
      newContacts[index].value = value;
    }
    setContacts(newContacts);
  };

  const toggleSchedule = (index) => {
    const newContacts = [...contacts];
    newContacts[index].showSchedule = !newContacts[index].showSchedule;
    setContacts(newContacts);
  };

  const handleScheduleTimeChange = (index, value) => {
    const newContacts = [...contacts];
    newContacts[index].send_time = value;
    setContacts(newContacts);
  };

  const addContactField = () => {
    setContacts([...contacts, { value: '', showSchedule: false, send_time: '' }]);
  };

  const handleRemoveContact = (index) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter((_, i) => i !== index));
    }
  };

  const handleSendAll = async () => {
    const filledContacts = contacts.filter(c => c.value.trim());
    if (filledContacts.length === 0) return alert('Enter a contact.');
    if (!videoId) return alert('No video generated yet.');

    setLoadingIndex('all');
    try {
      const userToken = localStorage.getItem('authToken');
      const sharePromises = filledContacts.map(contact =>
        axios.post(
          `${BASE_URL}/share-video`,
          {
            share_id: shareId,
            video_id: videoId,
            event_id: eventId,
            recipient_email: contact.value,
            message: 'Your QR code is ready!',
            send_time: contact.send_time || null,
          },
          { headers: { Authorization: `Bearer ${userToken}` } }
        )
      );
      await Promise.all(sharePromises);
      setMessage(`Successfully invited ${filledContacts.length} guest(s)!`);
      setDialogOpen(true);
    } catch (error) {
      alert('Error sharing video. Please try again.');
    } finally {
      setLoadingIndex(null);
    }
  };


  return (
    <Container maxWidth="md">
      <InvitePaper elevation={0}>

        {/* ─── Header ─── */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" sx={{ fontFamily: "'DM Serif Display', serif", color: T.ink }}>
              Invite Guests
            </Typography>
            <Typography variant="body2" sx={{ color: T.inkLight }}>
              Share access via Email or Phone Number
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* FIX: tab buttons are now ABOVE their respective content */}
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button
            onClick={() => setTab('invite')}
            variant={tab === 'invite' ? 'contained' : 'outlined'}
            sx={{
              borderRadius: '50px',
              textTransform: 'none',
              fontFamily: "'DM Sans'",
              bgcolor: tab === 'invite' ? T.amber : 'transparent',
              color: tab === 'invite' ? '#fff' : T.inkMid,
              borderColor: T.border,
              '&:hover': { bgcolor: tab === 'invite' ? T.amberLight : 'rgba(194,130,60,0.05)' },
            }}
          >
            Invite Guests
          </Button>
          <Button
            onClick={() => setTab('photos')}
            variant={tab === 'photos' ? 'contained' : 'outlined'}
            sx={{
              borderRadius: '50px',
              textTransform: 'none',
              fontFamily: "'DM Sans'",
              bgcolor: tab === 'photos' ? T.amber : 'transparent',
              color: tab === 'photos' ? '#fff' : T.inkMid,
              borderColor: T.border,
              '&:hover': { bgcolor: tab === 'photos' ? T.amberLight : 'rgba(194,130,60,0.05)' },
            }}
          >
            📸 Photo Requests
          </Button>
        </Stack>

        {/* ─── Tab: Invite Guests ─── */}
        {tab === 'invite' && (
          <>
            <Box sx={{ maxHeight: '400px', overflowY: 'auto', px: 1, mb: 3 }}>
              {contacts.map((contact, index) => (
                <GuestRow key={index}>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* Email / Phone input */}
                    <Box sx={{ flex: contact.showSchedule ? '1 1 200px' : '1 1 auto' }}>
                      <TextField
                        fullWidth
                        variant="standard"
                        placeholder="Email or Phone"
                        value={contact.value}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                        InputProps={{
                          disableUnderline: true,
                          startAdornment: (
                            <EmailIcon sx={{ color: T.amber, mr: 1, fontSize: 20 }} />
                          ),
                          sx: { fontSize: '0.95rem', p: '8px 0' },
                        }}
                      />
                    </Box>

                    {/* Schedule picker */}
                    {contact.showSchedule && (
                      <Box sx={{ flex: '1 1 180px' }}>
                        <TextField
                          type="datetime-local"
                          fullWidth
                          variant="standard"
                          value={contact.send_time}
                          onChange={(e) => handleScheduleTimeChange(index, e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            disableUnderline: true,
                            sx: { fontSize: '0.85rem' },
                          }}
                        />
                      </Box>
                    )}

                    {/* Action icons */}
                    <Stack direction="row" spacing={1}>
                      <Tooltip title={contact.showSchedule ? 'Remove Schedule' : 'Schedule Send'}>
                        <IconButton
                          size="small"
                          onClick={() => toggleSchedule(index)}
                          sx={{ color: contact.showSchedule ? T.amber : T.inkLight }}
                        >
                          <ScheduleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveContact(index)}
                        disabled={contacts.length === 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </GuestRow>
              ))}
            </Box>

            <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
              <ActionButton startIcon={<AddIcon />} onClick={addContactField}>
                Add Recipient
              </ActionButton>
              <ActionButton
                $primary
                startIcon={
                  loadingIndex === 'all' ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
                onClick={handleSendAll}
                disabled={loadingIndex === 'all'}
              >
                {loadingIndex === 'all' ? 'Sending…' : 'Send All Invites'}
              </ActionButton>
            </Stack>
          </>
        )}

        {/* ─── Tab: Photo Requests ─── */}
        {tab === 'photos' && (
          <PhotoRequestReview
            eventId={eventId}
            userId={userId}
            eventName={eventName}
            onPhotosAdded={onPhotosAdded}
            onRegenerateVideo={() => {
              /* call your existing generate-video handler here */
            }}
          />
        )}

      </InvitePaper>

      {/* ─── Success Dialog ─── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle
          sx={{ fontFamily: "'DM Serif Display', serif", textAlign: 'center', color: T.amber }}
        >
          Success
        </DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ color: T.inkMid }}>
            {message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <ActionButton $primary onClick={() => { setDialogOpen(false); handleClose(); }}>
            Return to Dashboard
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AccessRequestForm;